interface CompleteListEnvelope<T> {
  status: string;
  data: T[];
  meta?: { count?: number | string | null; offset?: number };
}

// Temporary offset-tie workaround: use only one complete response, never append partial probes.
export default async function readCompleteList<T>(
  request: (limit: number) => Promise<CompleteListEnvelope<T>>,
  identity: (row: T) => string,
): Promise<T[]> {
  let limit = 100;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop -- the response count sets the next request size
    const r = await request(limit);
    if (r?.status !== 'success' || !Array.isArray(r.data)) throw new Error('List could not be fully loaded.');
    if (r.meta?.count == null || r.meta.count === '') throw new Error('List total count unavailable.');
    const count = Number(r.meta.count);
    if (!Number.isSafeInteger(count) || count < 0) throw new Error('List count is invalid.');
    if (r.meta.offset != null && Number(r.meta.offset) !== 0) throw new Error('List did not start at zero.');
    const seen = new Set<string>();
    r.data.forEach((row) => {
      const id = identity(row);
      if (!id || seen.has(id)) throw new Error('List contains missing or repeated identities.');
      seen.add(id);
    });
    if (r.data.length > count) throw new Error('List exceeds reported count.');
    if (r.data.length === count) return r.data;
    if (count <= limit) throw new Error('List was clamped or incomplete.');
    limit = count;
  }
  throw new Error('List kept growing while loading. Please retry.');
}
